import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../../components/layout/view/ViewHeader";
import EmptyState from "../../../../../components/feedback/EmptyState";
import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import VideoThumbnailCard from "../../../../../components/cards/thumbnails/VideoThumbnailCard";

import { useGroupMemberVideos } from "../hooks/useGroupMemberVideos";

import "./GroupMemberVideosView.css";

const DEFAULT_LIMIT = 12;
const PREVIEW_LIMIT = 5;

export default function GroupMemberVideosView({
  groupTag,
  limit = DEFAULT_LIMIT,
  preview = false,
  onContentLoaded,
}) {
  const navigate = useNavigate();

  const pageLimit = preview
    ? PREVIEW_LIMIT
    : Math.max(1, Number(limit) || DEFAULT_LIMIT);

  const {
    videoThumbs,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMoreMemberGroupVideos,
  } = useGroupMemberVideos(groupTag, pageLimit);

  const videoList = Array.isArray(videoThumbs) ? videoThumbs : [];
  const videos = preview ? videoList.slice(0, PREVIEW_LIMIT) : videoList;

  const errorMessage = typeof error === "string" ? error : error?.message || "";

  useEffect(() => {
    if (!preview || loading) return;

    onContentLoaded?.({
      hasContent: videos.length > 0,
      error: Boolean(errorMessage),
    });
  }, [preview, loading, videos.length, errorMessage, onContentLoaded]);

  const handleOpenVideo = (video) => {
    if (!video?.videoTag || video.readyToWatch === false) return;

    const status = String(video.hlsStatus || "").toLowerCase();

    if (["processing", "pending"].includes(status)) return;

    navigate(`/watch/${encodeURIComponent(video.videoTag)}`);
  };

  const handleLoadMore = async () => {
    if (preview || loading || loadingMore || !hasMore) return;

    await loadMoreMemberGroupVideos();
  };

  if (preview && !loading && !videos.length && !errorMessage) {
    return null;
  }

  return (
    <ViewContainer>
      <ViewHeader
        title={`Group Videos (${videos.length})`}
        description="Videos shared with this group."
      />

      {errorMessage && videos.length > 0 && (
        <div className="group-member-videos__alert" role="alert">
          {errorMessage}
        </div>
      )}

      <div>
        {!loading && !videos.length ? (
          !preview && (
            <EmptyState
              title={
                errorMessage ? "Unable to load videos" : "No videos available"
              }
              description={
                errorMessage ||
                "Videos shared with this group will appear here."
              }
            />
          )
        ) : (
          <ContentGrid
            items={videos}
            loading={loading && !videos.length}
            columns={4}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            skeletonCount={preview ? PREVIEW_LIMIT : 8}
            renderItem={(video) => {
              const status = String(video.hlsStatus || "").toLowerCase();

              const disabled =
                video.readyToWatch === false ||
                ["processing", "pending"].includes(status);

              return (
                <VideoThumbnailCard
                  key={video.videoId ?? video.videoTag}
                  video={video}
                  disabled={disabled}
                  onClick={handleOpenVideo}
                />
              );
            }}
          />
        )}
      </div>

      {!preview && hasMore && videos.length > 0 && (
        <div className="group-member-videos__footer">
          <button
            type="button"
            className="group-member-videos__load-more"
            disabled={loading || loadingMore}
            onClick={handleLoadMore}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </ViewContainer>
  );
}
